import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthUser, Company, Member, Membership, Role } from '@buildmate/core';
import { type Principal } from '@buildmate/core';
import { usePorts } from './ports-context.tsx';
import { useAuth } from './use-auth.ts';

export interface Session {
  readonly user: AuthUser;
  readonly membership: Membership;
  readonly company: Company | null;
  /** Null until the role document loads; treat as "no permissions yet". */
  readonly principal: Principal | null;
  readonly memberships: readonly Membership[];
  /** Switch which company this session is acting in. */
  readonly switchCompany: (companyId: string) => Promise<void>;
}

type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'no-company'; user: AuthUser }
  | { status: 'ready'; session: Session };

const SessionContext = createContext<SessionState>({ status: 'loading' });

/**
 * Resolves who is signed in, which company they are acting in, and what they
 * may do.
 *
 * Kept in one place so a route guard, the navigation and a feature screen all
 * read the same answer rather than each fetching their own and disagreeing.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const ports = usePorts();
  const { user, memberships, loading } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);

  const active = useMemo(() => memberships.filter((m) => m.status === 'active'), [memberships]);

  // Restore the previously chosen company. Someone who belongs to two
  // companies — a sub working for two contractors, or anyone who accepted an
  // invite while already owning a company — would otherwise be dropped into
  // whichever happened to load first, with no way to reach the other.
  useEffect(() => {
    let cancelled = false;
    void ports.auth.activeCompanyId().then((stored) => {
      if (!cancelled) setActiveCompanyId(stored);
    });
    return () => {
      cancelled = true;
    };
  }, [ports, user]);

  const membership = useMemo(() => {
    const chosen = active.find((m) => m.companyId === activeCompanyId);
    // A stored id that no longer matches an active membership (access
    // revoked, or a different account signed in) falls back rather than
    // leaving the session stuck on a company it cannot reach.
    return chosen ?? active[0] ?? null;
  }, [active, activeCompanyId]);

  const switchCompany = useCallback(
    async (companyId: string) => {
      await ports.auth.setActiveCompany(companyId);
      setActiveCompanyId(companyId);
    },
    [ports],
  );

  useEffect(() => {
    if (!user || !membership) {
      setCompany(null);
      setPrincipal(null);
      return;
    }
    let cancelled = false;
    const scope = { companyId: membership.companyId };

    void Promise.all([
      ports.data.collection<Role>(scope, 'roles').get(membership.roleId),
      ports.data.collection<Member>(scope, 'members').get(user.uid),
      ports.data.collection<Company>(scope, 'companies').get(membership.companyId),
    ]).then(([role, member, loadedCompany]) => {
      if (cancelled) return;
      setCompany(loadedCompany);
      if (!role || !member) {
        setPrincipal(null);
        return;
      }
      setPrincipal({
        uid: user.uid,
        companyId: membership.companyId,
        kind: member.kind,
        status: member.status,
        jobAccess: member.jobAccess,
        jobIds: member.jobIds ?? [],
        permissions: { modules: role.modules, flags: role.flags },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [ports, user, membership]);

  const value: SessionState = useMemo(() => {
    if (loading) return { status: 'loading' };
    if (!user) return { status: 'signed-out' };
    if (!membership) return { status: 'no-company', user };
    return {
      status: 'ready',
      session: { user, membership, company, principal, memberships, switchCompany },
    };
  }, [loading, user, membership, company, principal, memberships, switchCompany]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionState(): SessionState {
  return useContext(SessionContext);
}

/** For screens that only render inside an authenticated company. */
export function useSession(): Session {
  const state = useSessionState();
  if (state.status !== 'ready') {
    throw new Error('useSession must be used inside a route that guarantees a session');
  }
  return state.session;
}
